import {
    HttpErrorResponse, HttpEvent,
    HttpHandler,
    HttpHeaderResponse,
    HttpInterceptor,
    HttpProgressEvent,
    HttpRequest,
    HttpResponse,
    HttpSentEvent,
    HttpUserEvent
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, finalize, switchMap, take } from 'rxjs/operators';
import { AuthService } from './auth.service';

@Injectable()
export class RequestInterceptor implements HttpInterceptor {

  isRefreshingToken = false;
  tokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>('');

  constructor(private authService: AuthService, private toastrService: ToastrService) { }

  addToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
    return req.clone({ setHeaders: { Authorization: 'Bearer ' + token } });
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpSentEvent | HttpHeaderResponse | HttpProgressEvent | HttpResponse<any> | HttpUserEvent<any> | HttpEvent<any>> {
    return next.handle(this.addToken(req, this.authService.getAccessToken())).pipe(
      catchError((error) => this.handleError(error, req, next)),
    );
  }

  private handleError(error: any, req: HttpRequest<any>, next: HttpHandler) {
    let errorMessage = '';

    if (error instanceof HttpErrorResponse) {
      if (error.error?.errors) {
        errorMessage = this.formatValidationErrors(error.error.errors);
      } else if (error.error?.message) {
        errorMessage = error.error.message;
      }

      if (!errorMessage) {
        switch (error.status) {
          case 400:
            errorMessage = 'A solicitação está mal formada. Verifique os dados enviados.';
            break;
          case 401:
            errorMessage = 'Você precisa estar autenticado para realizar esta ação.';
            return this.handle401Error(req, next);
          case 403:
            errorMessage = 'Você não tem permissão para acessar esta página. Entre em contato com o administrador do sistema.';
            break;
          case 404:
            errorMessage = 'Página não encontrada.';
            break;
          case 408:
            errorMessage = 'A requisição demorou muito para ser processada. Tente novamente.';
            break;
          case 422:
            errorMessage = 'Um ou mais campos enviados são inválidos ou não podem ser processados. Por favor, verifique as informações enviadas e tente novamente.';
            break;
          case 500:
            errorMessage = 'Erro interno no servidor. Entre em contato com o administrador do sistema e tente novamente mais tarde.';
            break;
          case 502:
            errorMessage = 'O servidor recebeu uma resposta inválida de outro servidor. Tente novamente.';
            break;
          case 503:
            errorMessage = 'O servidor está temporariamente indisponível. Tente novamente mais tarde.';
            break;
          case 504:
            errorMessage = 'O servidor demorou demais para responder. Tente novamente.';
            break;
          default:
            errorMessage = 'Ocorreu um erro inesperado.';
            break;
        }
      } else {
        if (error.status === 401) {
          return this.handle401Error(req, next);
        }
      }
    } else if (error.name === 'TimeoutError') {
      errorMessage = 'A requisição expirou. Tente novamente.';
    } else if (error.name === 'TypeError') {
      errorMessage = 'Falha de conexão. Verifique sua internet e tente novamente.';
    } else {
      errorMessage = 'Ocorreu um erro inesperado.';
    }

    this.toastrService.error(errorMessage, 'Erro!', { enableHtml: true });
    return throwError(error);
  }

  private formatValidationErrors(errors: any): string {
    if (!errors || typeof errors !== 'object') {
      return '';
    }

    const messages: string[] = [];

    for (const [field, value] of Object.entries(errors)) {
      if (Array.isArray(value)) {
        if (value.length) {
          const text = value.filter(Boolean).join(', ');
          messages.push(field === 'base' ? text : `${field}: ${text}`);
        }
      } else if (value && typeof value === 'object') {
        const nested = this.formatValidationErrors(value)
          .split('\n')
          .map(line => `  ${line}`)
          .join('<br>');
        messages.push(`${field}:<br>${nested}`);
      } else if (value !== undefined && value !== null) {
        messages.push(`${field}: ${value}`);
      }
    }

    return messages.join('<br>');
  }

  private handle401Error(req: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshingToken) {
      this.isRefreshingToken = true;
      this.tokenSubject.next('');

      return this.authService.silentRefreshToken().pipe(
        switchMap((newToken: any) => {
          if (newToken) {
            this.tokenSubject.next(newToken);
            return next.handle(this.addToken(req, newToken));
          }

          this.authService.signOut();
          return throwError('');
        }),
        catchError(error => {
          this.authService.signOut();
          return throwError(error);
        }),
        finalize(() => {
          this.isRefreshingToken = false;
        }));
    } else {
      return this.tokenSubject.pipe(
        filter(token => token != null),
        take(1),
        switchMap(token => {
          return next.handle(this.addToken(req, token));
        }));
    }
  }

}
